import { useEffect, useState } from "react";

import { toast, toastWarning } from "@alpacahq/ui/old";

import {
  useGetAsset,
  useGetClock,
  useGetMargin,
  useGetPositions,
  useMarketDataFeed,
} from "~/v2/api/hooks";
import {
  getOptionsContract,
  getQuote,
  type OptionContract,
  type Quote,
} from "~/v2/api/rest/data";
import {
  MARKET_DATA_POLLING_INTERVAL,
  POSITIONS_POLLING_INTERVAL,
} from "~/v2/constants";
import { useDebouncedValue } from "~/v2/hooks/useDebouncedValue";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { getIsContractSymbol } from "./shared/utils";

const DEBOUNCE_TIME = 300;
const TOAST_ID = "symbol-not-found";
const TOAST_DURATION = 2 * 1000;

interface UseOrderTicketControllerArgs {
  initialSymbol?: string;
}

export const useOrderTicketController = ({
  initialSymbol,
}: UseOrderTicketControllerArgs) => {
  const [symbol, setSymbol] = useState<string>(initialSymbol ?? "");
  const [quote, setQuote] = useState<Quote | undefined>(undefined);
  const [contract, setContract] = useState<OptionContract | undefined>(
    undefined,
  );

  // autoFocus keeps focus on symbol input as the OrderTicket changes based on asset type
  const [isSymbolInputAutoFocus, setIsSymbolInputAutoFocus] = useState(false);

  const debouncedSymbol = useDebouncedValue(symbol, DEBOUNCE_TIME);
  const isContract = getIsContractSymbol(symbol);
  const isDebouncedContract = getIsContractSymbol(debouncedSymbol);
  const isCryptoSymbol = symbol.includes("/");

  const { currentAccount, product } = useAccountContext();

  const { asset, isError: isAssetError } = useGetAsset({
    symbol: debouncedSymbol,
    options: {
      enabled: !!debouncedSymbol && !isDebouncedContract,
      // Handled with a custom "not found" warning below; suppress the shared toast.
      meta: { showErrorToast: false },
    },
  });

  useEffect(() => {
    if (isAssetError && debouncedSymbol && !isDebouncedContract) {
      toastWarning(`${debouncedSymbol} not found`, {
        toastId: TOAST_ID,
        autoClose: TOAST_DURATION,
      });
    }
  }, [isAssetError, debouncedSymbol, isDebouncedContract]);

  const { clock } = useGetClock({
    options: { staleTime: -1 },
  });

  const { latestFeed, feedLabel } = useMarketDataFeed(
    asset?.attributes?.includes("overnight_tradable"),
  );

  const { margin } = useGetMargin({
    accountID: currentAccount?.id,
    paper: product !== "live",
    options: {
      enabled: !!currentAccount?.id,
    },
  });

  const { positions } = useGetPositions({
    accountID: currentAccount?.id,
    product,
    options: {
      enabled: !!currentAccount?.id,
      refetchInterval: POSITIONS_POLLING_INTERVAL,
    },
  });

  const onSymbolChange = (symbol: string) => {
    setSymbol(symbol.toUpperCase());
    // If a user ever types in symbol give symbol inputs autofocus
    // This keeps focus on the symbol input as the order ticket changes based on asset type
    if (!isSymbolInputAutoFocus) {
      setIsSymbolInputAutoFocus(true);
    }
  };

  const fetchContract = async () => {
    if (!isDebouncedContract || !debouncedSymbol) {
      return;
    }

    try {
      const contract = await getOptionsContract({ symbol: debouncedSymbol });
      return contract;
    } catch (error) {
      console.error("Error fetching contract", error);
      return;
    }
  };

  const fetchQuote = async () => {
    if (!debouncedSymbol) {
      return quote;
    }

    try {
      const quote = await getQuote(debouncedSymbol, latestFeed);
      return quote;
    } catch (error) {
      console.error("Error fetching quote", error);
      return;
    }
  };

  const refetchQuote = async () => {
    const quote = await fetchQuote();
    setQuote(quote);
  };

  const fetchSymbolData = async () => {
    if (!debouncedSymbol) {
      return;
    }

    try {
      const [quote, contract] = await Promise.all([
        fetchQuote(),
        fetchContract(),
      ]);

      if (isDebouncedContract && !contract) {
        toastWarning(`${debouncedSymbol} not found`, {
          toastId: TOAST_ID,
          autoClose: TOAST_DURATION,
        });
      }

      setQuote(quote);
      setContract(contract);
    } catch (error) {
      console.error("Fetch failed", error);
    }
  };

  /**
   * useQuery just isn't fast enough at refetching data as the symbol changes.
   * debouncedSymbol keeps us from fetching incomplete symbols.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: debouncedSymbol and latestFeed are the triggers that restart the market-data polling interval; the body only writes them. fetchSymbolData is redefined every render, so listing it would tear down and restart the interval on every render.
  useEffect(() => {
    toast.dismiss(TOAST_ID);
    fetchSymbolData();

    const interval = setInterval(fetchSymbolData, MARKET_DATA_POLLING_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, [debouncedSymbol, latestFeed]);

  // This component is semi-controlled by parent
  // If the parent updates initialSymbol reset symbol state
  //
  // Only initialSymbol belongs in the dependencies. Reading symbol here to skip
  // a redundant set would force it into the array too, and the effect would then
  // re-run on every keystroke and put initialSymbol back, so a route like
  // /trade/TSLA could never be typed away from. setSymbol already bails out when
  // the value is unchanged, so the comparison bought nothing.
  useEffect(() => {
    if (initialSymbol) {
      setSymbol(initialSymbol);
    }
  }, [initialSymbol]);

  return {
    product,
    account: currentAccount,
    clock,
    symbol,
    onSymbolChange,
    isSymbolInputAutoFocus,
    isContractSymbol: isContract,
    isCryptoSymbol,
    margin: margin ?? undefined,
    positions,
    quote: quote?.symbol === symbol ? quote : undefined,
    refetchQuote,
    asset: asset?.symbol === symbol ? asset : undefined,
    contract: contract?.symbol === symbol ? contract : undefined,
    feedLabel,
  };
};

export type UseOrderTicketControllerReturn = ReturnType<
  typeof useOrderTicketController
>;
